// src/pages/ELearning/TaskBenefitDetail.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  Loader2, AlertCircle, Award, Calendar, User, FileText, Play, CheckCircle, XCircle, Edit, Download, RefreshCw
} from 'lucide-react';

interface TaskBenefit {
  _id: string;
  title: string;
  description: string;
  points: number;
  deadline: string;
  status: 'open' | 'in_progress' | 'completed' | 'approved' | 'rejected';
  image?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
  };
  report?: string;
  reportFile?: {
    name: string;
    size: number;
    mimeType: string;
  };
  completedAt?: string;
  createdAt: string;
}

const TaskBenefitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [task, setTask] = useState<TaskBenefit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState('');
  const [showReportInput, setShowReportInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);

  const fetchTask = async () => {
    try {
      const res = await api.get(`/elearning/task-benefits/${id}`);
      setTask(res.data);
    } catch (err: any) {
      console.error('Ошибка загрузки задачи:', err);
      setError(err.response?.data?.error || 'Не удалось загрузить задачу');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
  }, [id]);

  const handleTakeTask = async () => {
    try {
      await api.post(`/elearning/task-benefits/${id}/take`);
      showToast('Задача взята в работу', 'success');
      fetchTask();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось взять задачу', 'error');
    }
  };

  const handleSubmitReport = async () => {
    if (!report.trim()) {
      showToast('Введите отчёт', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/elearning/task-benefits/${id}/report`, { report });
      showToast('Отчёт отправлен', 'success');
      setShowReportInput(false);
      setReport('');
      fetchTask();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось отправить отчёт', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/elearning/task-benefits/${id}/approve`);
      showToast('Задача одобрена, баллы начислены', 'success');
      fetchTask();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось одобрить задачу', 'error');
    }
  };

  const handleReject = async () => {
    try {
      await api.post(`/elearning/task-benefits/${id}/reject`);
      showToast('Задача отклонена', 'success');
      fetchTask();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось отклонить задачу', 'error');
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionComment.trim()) {
      showToast('Введите комментарий к доработке', 'error');
      return;
    }
    try {
      await api.post(`/elearning/task-benefits/${id}/revise`, { comment: revisionComment });
      showToast('Задача отправлена на доработку', 'success');
      setShowRevisionInput(false);
      setRevisionComment('');
      fetchTask();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось отправить на доработку', 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; color: string; bg: string }> = {
      open: { label: 'Открыта', color: '#2575fc', bg: 'rgba(37,117,252,0.1)' },
      in_progress: { label: 'В работе', color: '#faad14', bg: 'rgba(250,173,20,0.1)' },
      completed: { label: 'Выполнена', color: '#52c41a', bg: 'rgba(82,196,26,0.1)' },
      approved: { label: 'Одобрена', color: '#2ecc71', bg: 'rgba(46,204,113,0.1)' },
      rejected: { label: 'Отклонена', color: '#ff4d4f', bg: 'rgba(255,77,79,0.1)' }
    };
    const s = map[status] || { label: status, color: '#666', bg: '#f0f0f0' };
    return (
      <span style={{ background: s.bg, color: s.color, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error || !task) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error || 'Задача не найдена'}</h2>
          <button onClick={() => navigate('/app/elearning/task-benefits')} style={styles.retryButton}>
            Вернуться к задачам
          </button>
        </div>
      </>
    );
  }

  const isCreator = String(task.createdBy?._id || task.createdBy) === String(user?.id);
  const isAssignee = task.assignedTo && String(task.assignedTo?._id) === String(user?.id);
  const canTake = task.status === 'open' && !isCreator;
  const canReport = task.status === 'in_progress' && isAssignee;
  const canApprove = task.status === 'completed' && isCreator;

  // Статус — цвет и иконка для hero
  const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
    open: { label: 'Открыта', color: '#2575fc', bg: 'rgba(37,117,252,0.12)', icon: '📋' },
    in_progress: { label: 'В работе', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: '⚡' },
    completed: { label: 'На проверке', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', icon: '📤' },
    approved: { label: 'Одобрена', color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: '✅' },
    rejected: { label: 'Отклонена', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: '❌' },
  };
  const sc = statusConfig[task.status] || statusConfig.open;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        .tb-detail { animation: fadeUp 0.5s ease-out; }
        .tb-card { animation: fadeUp 0.5s ease-out backwards; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .tb-card:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(31, 38, 135, 0.1); }
        .tb-btn { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        .tb-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(102,126,234,0.3); }
        .tb-btn:active { transform: translateY(0); }
      `}</style>

      <div className="tb-detail" style={styles.container}>

        {/* ─── Кнопка назад ─── */}
        <button onClick={() => navigate('/app/elearning/task-benefits')} style={styles.backBtn}>
          ← Назад к задачам
        </button>

        {/* ─── HERO ─── */}
        <div style={styles.hero}>
          <div style={styles.heroLeft}>
            {task.image ? (
              <img src={task.image} alt={task.title} style={styles.heroImage} />
            ) : (
              <div style={styles.heroImagePlaceholder}>🎯</div>
            )}
          </div>

          <div style={styles.heroRight}>
            <div style={{ ...styles.statusPill, background: sc.bg, color: sc.color }}>
              <span>{sc.icon}</span>
              <span>{sc.label}</span>
            </div>

            <h1 style={styles.heroTitle}>{task.title}</h1>

            {task.description && (
              <p style={styles.heroDescription}>{task.description}</p>
            )}
          </div>
        </div>

        {/* ─── МЕТАДАННЫЕ ─── */}
        <div style={styles.metaGrid}>
          <div className="tb-card" style={styles.metaCard}>
            <div style={{ ...styles.metaIconWrap, background: 'rgba(241,196,15,0.12)' }}>
              <Award size={20} color="#f1c40f" />
            </div>
            <div>
              <div style={styles.metaLabel}>Награда</div>
              <div style={styles.metaValue}>{task.points} баллов</div>
            </div>
          </div>

          <div className="tb-card" style={styles.metaCard}>
            <div style={{ ...styles.metaIconWrap, background: 'rgba(52,152,219,0.12)' }}>
              <Calendar size={20} color="#3498db" />
            </div>
            <div>
              <div style={styles.metaLabel}>Срок</div>
              <div style={styles.metaValue}>
                {new Date(task.deadline).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
            </div>
          </div>

          <div className="tb-card" style={styles.metaCard}>
            <div style={{ ...styles.metaIconWrap, background: 'rgba(155,89,182,0.12)' }}>
              <User size={20} color="#9b59b6" />
            </div>
            <div>
              <div style={styles.metaLabel}>Создатель</div>
              <div style={styles.metaValue}>{task.createdBy?.name || '—'}</div>
            </div>
          </div>

          <div className="tb-card" style={styles.metaCard}>
            <div style={{ ...styles.metaIconWrap, background: 'rgba(46,204,113,0.12)' }}>
              <User size={20} color="#2ecc71" />
            </div>
            <div>
              <div style={styles.metaLabel}>Исполнитель</div>
              <div style={styles.metaValue}>{task.assignedTo?.name || '—'}</div>
            </div>
          </div>
        </div>

        {/* ─── ТАЙМЛАЙН СТАТУСОВ ─── */}
        <div className="tb-card" style={styles.timelineCard}>
          <h3 style={styles.sectionTitle}>История задачи</h3>
          <div style={styles.timeline}>
            <div style={styles.timelineItem}>
              <div style={styles.timelineDot} />
              <div>
                <div style={styles.timelineLabel}>Создана</div>
                <div style={styles.timelineDate}>{new Date(task.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>

            {task.assignedTo && (
              <div style={styles.timelineItem}>
                <div style={{ ...styles.timelineDot, background: '#f59e0b' }} />
                <div>
                  <div style={styles.timelineLabel}>Взята в работу</div>
                  <div style={styles.timelineDate}>{task.assignedTo.name}</div>
                </div>
              </div>
            )}

            {task.report && (
              <div style={styles.timelineItem}>
                <div style={{ ...styles.timelineDot, background: '#8b5cf6' }} />
                <div>
                  <div style={styles.timelineLabel}>Отчёт отправлен</div>
                  <div style={styles.timelineDate}>Ожидает проверки</div>
                </div>
              </div>
            )}

            {task.completedAt && (
              <div style={styles.timelineItem}>
                <div style={{ ...styles.timelineDot, background: '#10b981' }} />
                <div>
                  <div style={styles.timelineLabel}>
                    {task.status === 'approved' ? 'Одобрена' : task.status === 'rejected' ? 'Отклонена' : 'Завершена'}
                  </div>
                  <div style={styles.timelineDate}>
                    {new Date(task.completedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── ОТЧЁТ ─── */}
        {task.report && (
          <div className="tb-card" style={{ ...styles.reportCard, borderColor: sc.color + '33' }}>
            <div style={{ ...styles.reportHeader, background: sc.bg }}>
              <FileText size={20} color={sc.color} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: sc.color }}>Отчёт о выполнении</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Предоставлен исполнителем</div>
              </div>
            </div>
            <div style={styles.reportBody}>
              <p style={styles.reportText}>{task.report}</p>

              {task.reportFile && (
                <a
                  href={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/elearning/task-benefits/${task._id}/report/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.fileLink}
                >
                  <Download size={16} />
                  <span style={{ marginLeft: 6 }}>{task.reportFile.name}</span>
                  <span style={{ marginLeft: 6, opacity: 0.6 }}>({(task.reportFile.size / 1024).toFixed(1)} КБ)</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* ─── ДЕЙСТВИЯ ─── */}
        <div style={styles.actions}>
          {canTake && (
            <button onClick={handleTakeTask} className="tb-btn" style={styles.primaryButton}>
              <Play size={16} /> Взять в работу
            </button>
          )}
          {canReport && !showReportInput && (
            <button onClick={() => setShowReportInput(true)} className="tb-btn" style={styles.primaryButton}>
              <FileText size={16} /> Отправить отчёт
            </button>
          )}
          {canApprove && (
            <>
              <button onClick={handleApprove} className="tb-btn" style={{ ...styles.primaryButton, background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <CheckCircle size={16} /> Одобрить
              </button>
              <button onClick={handleReject} className="tb-btn" style={{ ...styles.primaryButton, background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                <XCircle size={16} /> Отклонить
              </button>
              <button onClick={() => setShowRevisionInput(true)} className="tb-btn" style={styles.secondaryButton}>
                <RefreshCw size={16} /> На доработку
              </button>
            </>
          )}
          {isCreator && task.status === 'open' && (
            <button onClick={() => navigate(`/app/elearning/task-benefits/${task._id}/edit`)} className="tb-btn" style={styles.secondaryButton}>
              <Edit size={16} /> Редактировать
            </button>
          )}
        </div>

        {/* ─── ФОРМА ОТЧЁТА ─── */}
        {showReportInput && (
          <div className="tb-card" style={styles.formCard}>
            <h3 style={styles.formTitle}>📝 Что было сделано?</h3>
            <textarea
              placeholder="Опишите результат вашей работы..."
              value={report}
              onChange={e => setReport(e.target.value)}
              style={styles.textarea}
              rows={5}
            />
            <div style={styles.formActions}>
              <button onClick={handleSubmitReport} disabled={submitting} className="tb-btn" style={styles.primaryButton}>
                {submitting ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={16} />}
                {submitting ? 'Отправка...' : 'Отправить'}
              </button>
              <button onClick={() => { setShowReportInput(false); setReport(''); }} style={styles.secondaryButton}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {/* ─── ФОРМА ДОРАБОТКИ ─── */}
        {showRevisionInput && (
          <div className="tb-card" style={styles.formCard}>
            <h3 style={styles.formTitle}>🔄 Что нужно доработать?</h3>
            <textarea
              placeholder="Опишите, что нужно исправить..."
              value={revisionComment}
              onChange={e => setRevisionComment(e.target.value)}
              style={styles.textarea}
              rows={4}
            />
            <div style={styles.formActions}>
              <button onClick={handleRequestRevision} className="tb-btn" style={styles.primaryButton}>
                <RefreshCw size={16} /> Отправить на доработку
              </button>
              <button onClick={() => { setShowRevisionInput(false); setRevisionComment(''); }} style={styles.secondaryButton}>
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto', padding: '0 8px' },

  backBtn: {
    background: 'none', border: 'none', color: '#667eea', fontWeight: 600,
    fontSize: 14, cursor: 'pointer', padding: '8px 0', marginBottom: 20,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },

  // ─── HERO ───
  hero: {
    display: 'flex', gap: 24, alignItems: 'center',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(245,247,255,0.9))',
    backdropFilter: 'blur(12px)',
    borderRadius: 24, padding: 28, marginBottom: 24,
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 12px 40px rgba(31, 38, 135, 0.08)',
    flexWrap: 'wrap',
  },
  heroLeft: { flexShrink: 0 },
  heroImage: {
    width: 140, height: 140, borderRadius: 20, objectFit: 'cover',
    boxShadow: '0 8px 24px rgba(31,38,135,0.15)',
  },
  heroImagePlaceholder: {
    width: 140, height: 140, borderRadius: 20,
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 56, color: '#fff',
  },
  heroRight: { flex: 1, minWidth: 280 },
  statusPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '5px 14px', borderRadius: 100, fontSize: 12, fontWeight: 700,
    marginBottom: 12, letterSpacing: 0.3,
  },
  heroTitle: {
    fontSize: 28, fontWeight: 800, margin: '0 0 10px 0',
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    lineHeight: 1.2,
  },
  heroDescription: {
    fontSize: 15, lineHeight: 1.55, color: '#4a4a6a', margin: 0,
  },

  // ─── МЕТА ───
  metaGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12, marginBottom: 24,
  },
  metaCard: {
    display: 'flex', gap: 12, alignItems: 'center',
    background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 16, padding: 16,
    boxShadow: '0 4px 12px rgba(31,38,135,0.04)',
  },
  metaIconWrap: {
    width: 42, height: 42, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  metaLabel: { fontSize: 11, color: '#8b8fa8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
  metaValue: { fontSize: 14, color: '#1a1a2e', fontWeight: 700, marginTop: 2 },

  // ─── ТАЙМЛАЙН ───
  timelineCard: {
    background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 20, padding: 24, marginBottom: 24,
    boxShadow: '0 4px 12px rgba(31,38,135,0.04)',
  },
  sectionTitle: { fontSize: 15, fontWeight: 800, margin: '0 0 16px 0', color: '#1a1a2e' },
  timeline: { display: 'flex', flexDirection: 'column', gap: 16 },
  timelineItem: { display: 'flex', gap: 14, alignItems: 'flex-start' },
  timelineDot: {
    width: 12, height: 12, borderRadius: '50%', background: '#667eea',
    marginTop: 4, flexShrink: 0,
    boxShadow: '0 0 0 4px rgba(102,126,234,0.15)',
  },
  timelineLabel: { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  timelineDate: { fontSize: 12, color: '#8b8fa8', marginTop: 2 },

  // ─── ОТЧЁТ ───
  reportCard: {
    background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
    border: '2px solid', borderRadius: 20, marginBottom: 24,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(31,38,135,0.04)',
  },
  reportHeader: {
    display: 'flex', gap: 12, alignItems: 'center', padding: '16px 20px',
  },
  reportBody: { padding: '20px' },
  reportText: {
    fontSize: 15, lineHeight: 1.6, color: '#2a2a4a', margin: '0 0 16px 0',
    whiteSpace: 'pre-wrap',
  },
  fileLink: {
    display: 'inline-flex', alignItems: 'center',
    color: '#667eea', textDecoration: 'none', fontWeight: 600, fontSize: 14,
    padding: '10px 16px', background: 'rgba(102,126,234,0.08)',
    borderRadius: 12,
  },

  // ─── ДЕЙСТВИЯ ───
  actions: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 },
  primaryButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: '#fff', border: 'none', borderRadius: 14,
    padding: '14px 24px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontWeight: 700, fontSize: 14,
    boxShadow: '0 4px 14px rgba(102,126,234,0.3)',
  },
  secondaryButton: {
    background: 'rgba(255,255,255,0.8)', color: '#667eea',
    border: '1.5px solid rgba(102,126,234,0.3)', borderRadius: 14,
    padding: '14px 24px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontWeight: 600, fontSize: 14,
  },

  // ─── ФОРМЫ ───
  formCard: {
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 20, padding: 24, marginBottom: 24,
    boxShadow: '0 8px 32px rgba(31,38,135,0.08)',
    animation: 'fadeUp 0.4s ease-out',
  },
  formTitle: { fontSize: 16, fontWeight: 800, margin: '0 0 16px 0', color: '#1a1a2e' },
  textarea: {
    width: '100%', padding: 16, borderRadius: 14,
    border: '1.5px solid rgba(102,126,234,0.2)',
    background: '#fff', fontSize: 14, resize: 'vertical',
    marginBottom: 16, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  formActions: { display: 'flex', gap: 12, flexWrap: 'wrap' },

  // ─── СТАРОЕ (retry) ───
  retryButton: {
    marginTop: 20, padding: '12px 24px',
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: '#fff', border: 'none', borderRadius: 14,
    cursor: 'pointer', fontWeight: 600,
  },
};

export default TaskBenefitDetail;